<template>
  <section v-if="booking.view.value === 'slots'" class="booking-section">
    <CardTitle>Availability for {{ booking.formatDate(currentDay, 'MMMM dd, yyyy') }}</CardTitle>
    <ol class="max-h-80 overflow-auto mt-8 space-y-1 text-sm leading-6 text-gray-500">
      <li
        v-for="event of slots"
        :key="event.id"
        :class="[
          'slot-item group',
          booking.selectedSlot.value === event.id ? 'slot-item--selected' : 'slot-item--default',
        ]"
        @click="booking.selectSlot(event)"
      >
        <div class="flex-auto">
          <p class="mt-0.5">
            {{ booking.formatDate(event.startTime, 'h:mm a') }} -
            {{ booking.formatDate(event.endTime, 'h:mm a') }}
          </p>
        </div>
      </li>
    </ol>
    <LoadingSpinner />
    <div v-if="slots.length == 0" class="empty-state">
      <vue-feather type="calendar" class="h-6 w-6 text-gray-400" />
      <p class="text-sm text-muted-foreground">No availability on this day</p>
    </div>
    <div :class="['actions', { invisible: booking.selectedSlot.value === '' }]">
      <Button variant="outline" class="mr-2" @click="booking.reset()">Cancel</Button>
      <Button data-track="clicked-reserve-slot-interview-button" @click="booking.openForm()">Reserve Slot</Button>
    </div>
  </section>

  <section v-else class="booking-section">
    <CardTitle class="mb-6">{{ booking.selectedSlot.value }}</CardTitle>

    <div class="space-y-4">
      <Field class="gap-1">
        <FieldLabel for="firstName">First Name</FieldLabel>
        <Input
          id="firstName"
          placeholder="Elon"
          :model-value="candidate.firstName"
          :disabled="!editable"
          @update:model-value="candidate.firstName = String($event)"
        />
      </Field>

      <Field class="gap-1">
        <FieldLabel for="lastName">Last Name</FieldLabel>
        <Input
          id="lastName"
          placeholder="Husk"
          :model-value="candidate.lastName"
          :disabled="!editable"
          @update:model-value="candidate.lastName = String($event)"
        />
      </Field>

      <Field class="gap-1">
        <FieldLabel for="phone">Phone Number</FieldLabel>
        <Input
          id="phone"
          type="tel"
          placeholder="+1 (281) 330 8004"
          :model-value="candidate.phone"
          :disabled="!editable"
          @update:model-value="candidate.phone = String($event)"
        />
      </Field>

      <Field class="gap-1">
        <FieldLabel for="email">Email</FieldLabel>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          :model-value="candidate.email"
          :disabled="!editable"
          @update:model-value="candidate.email = String($event)"
        />
      </Field>
    </div>

    <div class="actions">
      <Button variant="outline" class="mr-2" @click="booking.reset()">Cancel</Button>
      <Button @click="emit('confirm', booking.selectedSlotStartTime.value, booking.selectedSlotEndTime.value)">
        {{ rescheduling ? 'Confirm Reschedule' : 'Confirm' }}
        <LocalLoadingSpinner />
      </Button>
    </div>
  </section>
</template>

<script setup lang="ts">
import LoadingSpinner from '../shared/LoadingSpinner.vue';
import { Button } from '@/components/ui/button';
import { CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { useInterviewBooking, type EventSlot } from './useInterviewBooking';

defineProps<{
  slots: EventSlot[];
  currentDay: Date;
  candidate: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  rescheduling: boolean;
  editable: boolean;
  booking: ReturnType<typeof useInterviewBooking>;
}>();

const emit = defineEmits<{
  confirm: [startTime: string, endTime: string];
}>();
</script>

<style scoped>
@reference 'tailwindcss';

.booking-section {
  @apply md:col-span-6 lg:col-span-4 mt-12 md:mt-0 md:pl-14;
}

.slot-item {
  @apply cursor-pointer flex items-center space-x-4 rounded-xl border border-transparent px-4 py-2 transition-colors;
}

.slot-item--selected {
  @apply bg-gray-900 text-white border-gray-900;
}

.slot-item--default {
  @apply hover:bg-gray-100 hover:border-gray-200;
}

.empty-state {
  @apply mt-8 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50/40 py-10;
}

.actions {
  @apply mt-4 flex justify-end;
}
</style>
